import * as React from 'react';
import styled from 'styled-components';

import { getFranchiseRevenue } from '../../api/dashboard';

const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
});

const IncomeBlock = () => {
  const [incomeData, setIncomeData] = React.useState<
    IncomeData
  >();

  React.useEffect(() => {
    getFranchiseRevenue().then(response => {
      setIncomeData(response.data);
    });
  }, []);

  return (
    <Wrapper>
      <Header>Income</Header>
      {incomeData && (
        <Content>
          <KpiRow>
            <KpiTile accent="#FF9441">
              <TileLabel>Proposals (Month)</TileLabel>
              <TileValue>
                {currencyFormat.format(
                  Number(incomeData?.projectedSales) || 0,
                )}
              </TileValue>
            </KpiTile>
            <KpiTile accent="#067775">
              <TileLabel>Sales (Month)</TileLabel>
              <TileValue>
                {currencyFormat.format(
                  Number(incomeData?.salesPerMonth) || 0,
                )}
              </TileValue>
            </KpiTile>
            <KpiTile accent="#832C96">
              <TileLabel>This Year</TileLabel>
              <TileValue>
                {currencyFormat.format(
                  Number(incomeData?.salesPerYear) || 0,
                )}
              </TileValue>
            </KpiTile>
          </KpiRow>

          <StatList>
            <StatRow>
              <StatLabel>Today&apos;s Followup</StatLabel>
              <StatValue>
                {incomeData?.dueTodayActivities ?? 0}
              </StatValue>
            </StatRow>
            <StatRow>
              <StatLabel>
                Total Number of Pending Activity
              </StatLabel>
              <StatValue>
                {incomeData?.pendingActivities ?? 0}
              </StatValue>
            </StatRow>
          </StatList>
        </Content>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 24px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
`;

const Header = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  margin-bottom: 20px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const KpiTile = styled.div<{ accent: string }>`
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-top: 3px solid ${({ accent }) => accent};
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const TileLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const TileValue = styled.div`
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatList = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: 0;
  }
`;

const StatLabel = styled.span`
  font-size: 0.875rem;
  color: var(--neutral-700);
`;

const StatValue = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--mainText);
`;

interface IncomeData {
  pendingActivities: number;
  dueTodayActivities: number;
  salesPerYear: number;
  salesPerMonth: number;
  projectedSales: number;
}

export default IncomeBlock;
